import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Clock, Save, Trash2, Plus, ShieldAlert, ArrowLeft, CalendarDays } from "lucide-react";

export const Route = createFileRoute("/_authenticated/account/openingstijden")({
  head: () => ({
    meta: [
      { title: "Openingstijden beheren — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OpeningHoursAdmin,
});

type Row = {
  id: string;
  day_order: number;
  label: string;
  hours: string;
  is_closed: boolean;
};

function OpeningHoursAdmin() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: roles } = useQuery({
    queryKey: ["my-roles", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []).map((r) => r.role as string);
    },
  });
  const isAdmin = !!roles?.includes("admin");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["opening_hours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("opening_hours")
        .select("id, day_order, label, hours, is_closed")
        .order("day_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const [draft, setDraft] = useState<Row[]>([]);
  useEffect(() => {
    if (rows) setDraft(rows);
  }, [rows]);

  const saveMut = useMutation({
    mutationFn: async (row: Row) => {
      const { error } = await supabase
        .from("opening_hours")
        .update({
          day_order: row.day_order,
          label: row.label,
          hours: row.hours,
          is_closed: row.is_closed,
        })
        .eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opening_hours"] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("opening_hours").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opening_hours"] }),
  });

  const addMut = useMutation({
    mutationFn: async () => {
      const nextOrder = Math.max(0, ...draft.map((d) => d.day_order)) + 1;
      const { error } = await supabase
        .from("opening_hours")
        .insert({ day_order: nextOrder, label: "Nieuwe regel", hours: "—", is_closed: false });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opening_hours"] }),
  });

  if (!isAdmin) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-start gap-3">
          <ShieldAlert className="size-5 shrink-0 text-amber-600" />
          <div>
            <h1 className="font-display text-lg font-bold text-amber-900">
              Alleen voor admins
            </h1>
            <p className="mt-1 text-sm text-amber-800">
              Deze pagina is alleen toegankelijk voor beheerders. Vraag een admin
              om de admin-rol aan uw account te koppelen.
            </p>
            <Link
              to="/account"
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-amber-900 hover:underline"
            >
              <ArrowLeft className="size-4" /> Terug naar mijn account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const updateField = (id: string, patch: Partial<Row>) =>
    setDraft((d) => d.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-xl bg-orange/10 text-orange">
          <Clock className="size-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">
            Openingstijden beheren
          </h1>
          <p className="text-sm text-text-secondary">
            Deze tijden verschijnen direct op de klantenservice-pagina.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border px-5 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
          <div className="grid grid-cols-12 gap-3">
            <span className="col-span-1">#</span>
            <span className="col-span-5">Label</span>
            <span className="col-span-4">Tijden</span>
            <span className="col-span-2 text-right">Acties</span>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 text-sm text-text-secondary">Laden…</div>
        ) : draft.length === 0 ? (
          <div className="p-6 text-sm text-text-secondary">Nog geen regels.</div>
        ) : (
          <ul className="divide-y divide-border">
            {draft.map((r) => {
              const original = rows?.find((o) => o.id === r.id);
              const dirty =
                !!original &&
                (original.label !== r.label ||
                  original.hours !== r.hours ||
                  original.day_order !== r.day_order ||
                  original.is_closed !== r.is_closed);
              return (
                <li key={r.id} className="px-5 py-3">
                  <div className="grid grid-cols-12 items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      className="col-span-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      value={r.day_order}
                      onChange={(e) =>
                        updateField(r.id, { day_order: Number(e.target.value) || 0 })
                      }
                    />
                    <input
                      className="col-span-5 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                      value={r.label}
                      onChange={(e) => updateField(r.id, { label: e.target.value })}
                      placeholder="bijv. Maandag - Vrijdag"
                    />
                    <div className="col-span-4 flex items-center gap-2">
                      <input
                        className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                        value={r.hours}
                        onChange={(e) => updateField(r.id, { hours: e.target.value })}
                        placeholder="bijv. 08:30 - 17:00"
                      />
                      <label className="flex items-center gap-1.5 text-xs text-text-secondary whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={r.is_closed}
                          onChange={(e) =>
                            updateField(r.id, { is_closed: e.target.checked })
                          }
                        />
                        gesloten
                      </label>
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <button
                        onClick={() => saveMut.mutate(r)}
                        disabled={!dirty || saveMut.isPending}
                        className="inline-flex items-center gap-1 rounded-md bg-orange px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Save className="size-3.5" />
                        Opslaan
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Regel "${r.label}" verwijderen?`)) deleteMut.mutate(r.id);
                        }}
                        className="inline-flex items-center justify-center rounded-md border border-border p-1.5 text-text-secondary transition hover:border-red-300 hover:text-red-600"
                        aria-label="Verwijderen"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="border-t border-border px-5 py-3">
          <button
            onClick={() => addMut.mutate()}
            disabled={addMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-xs font-semibold text-text-secondary transition hover:border-orange hover:text-orange disabled:opacity-50"
          >
            <Plus className="size-3.5" />
            Regel toevoegen
          </button>
        </div>
      </div>

      <p className="text-xs text-text-secondary">
        Tip: zet <strong>Volgorde</strong> op een lager nummer om een regel hoger
        in de lijst te tonen. Vink <strong>gesloten</strong> aan als de dag dicht
        is — de tekst (bijv. "Gesloten") krijgt dan een gedempte stijl op de
        klantenservice-pagina.
      </p>

      <ExceptionsSection />
    </div>
  );
}

type Exception = {
  id: string;
  exception_date: string;
  label: string;
  hours: string;
  is_closed: boolean;
  note: string | null;
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateNL(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function ExceptionsSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    exception_date: "",
    label: "",
    hours: "Gesloten",
    is_closed: true,
    note: "",
  });

  const { data: exceptions, isLoading } = useQuery({
    queryKey: ["opening_hours_exceptions_admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("opening_hours_exceptions")
        .select("id, exception_date, label, hours, is_closed, note")
        .order("exception_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Exception[];
    },
  });

  const addMut = useMutation({
    mutationFn: async () => {
      if (!form.exception_date || !form.label.trim()) {
        throw new Error("Vul minimaal een datum en label in.");
      }
      const { error } = await supabase.from("opening_hours_exceptions").insert({
        exception_date: form.exception_date,
        label: form.label.trim(),
        hours: form.is_closed ? "Gesloten" : form.hours.trim() || "—",
        is_closed: form.is_closed,
        note: form.note.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setForm({ exception_date: "", label: "", hours: "Gesloten", is_closed: true, note: "" });
      qc.invalidateQueries({ queryKey: ["opening_hours_exceptions_admin"] });
      qc.invalidateQueries({ queryKey: ["public-opening-hours-exceptions"] });
    },
  });

  const updateMut = useMutation({
    mutationFn: async (e: Exception) => {
      const { error } = await supabase
        .from("opening_hours_exceptions")
        .update({
          exception_date: e.exception_date,
          label: e.label,
          hours: e.is_closed ? "Gesloten" : e.hours,
          is_closed: e.is_closed,
          note: e.note,
        })
        .eq("id", e.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opening_hours_exceptions_admin"] });
      qc.invalidateQueries({ queryKey: ["public-opening-hours-exceptions"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("opening_hours_exceptions")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opening_hours_exceptions_admin"] });
      qc.invalidateQueries({ queryKey: ["public-opening-hours-exceptions"] });
    },
  });

  const today = todayISO();
  const upcoming = (exceptions ?? []).filter((e) => e.exception_date >= today);
  const past = (exceptions ?? []).filter((e) => e.exception_date < today);

  return (
    <div className="space-y-4 pt-6">
      <div className="flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-xl bg-orange/10 text-orange">
          <CalendarDays className="size-5" />
        </div>
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">
            Feestdagen & uitzonderingen
          </h2>
          <p className="text-sm text-text-secondary">
            Tijdelijk afwijkende openingstijden voor specifieke dagen (bv.
            Eerste Kerstdag, vakantieperiode, studiedag).
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-foreground">Nieuwe uitzondering</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-12">
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-text-secondary">Datum</label>
            <input
              type="date"
              min={today}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
              value={form.exception_date}
              onChange={(e) => setForm((f) => ({ ...f, exception_date: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-4">
            <label className="block text-xs font-medium text-text-secondary">Label</label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
              placeholder="bv. Eerste Kerstdag"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-text-secondary">Tijden</label>
            <input
              disabled={form.is_closed}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm disabled:bg-muted/50 disabled:text-text-secondary"
              placeholder="bv. 10:00 - 14:00"
              value={form.hours}
              onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-2 flex items-end">
            <label className="flex items-center gap-2 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={form.is_closed}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    is_closed: e.target.checked,
                    hours: e.target.checked ? "Gesloten" : f.hours === "Gesloten" ? "" : f.hours,
                  }))
                }
              />
              gesloten
            </label>
          </div>
          <div className="sm:col-span-12">
            <label className="block text-xs font-medium text-text-secondary">
              Notitie (optioneel)
            </label>
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
              placeholder="bv. Servicedesk gesloten, storingsdienst wel bereikbaar"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </div>
        </div>
        {addMut.error && (
          <p className="mt-2 text-xs text-red-600">
            {(addMut.error as Error).message}
          </p>
        )}
        <button
          onClick={() => addMut.mutate()}
          disabled={addMut.isPending}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-orange px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-orange/90 disabled:opacity-50"
        >
          <Plus className="size-3.5" />
          Uitzondering toevoegen
        </button>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border px-5 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
          Aankomend
        </div>
        {isLoading ? (
          <div className="p-5 text-sm text-text-secondary">Laden…</div>
        ) : upcoming.length === 0 ? (
          <div className="p-5 text-sm text-text-secondary">
            Geen aankomende uitzonderingen.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {upcoming.map((e) => (
              <ExceptionRow
                key={e.id}
                exception={e}
                onSave={(next) => updateMut.mutate(next)}
                onDelete={() => {
                  if (confirm(`Uitzondering "${e.label}" verwijderen?`)) deleteMut.mutate(e.id);
                }}
              />
            ))}
          </ul>
        )}

        {past.length > 0 && (
          <>
            <div className="border-y border-border px-5 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
              Verlopen
            </div>
            <ul className="divide-y divide-border">
              {past.map((e) => (
                <ExceptionRow
                  key={e.id}
                  exception={e}
                  onSave={(next) => updateMut.mutate(next)}
                  onDelete={() => {
                    if (confirm(`Uitzondering "${e.label}" verwijderen?`)) deleteMut.mutate(e.id);
                  }}
                  muted
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function ExceptionRow({
  exception,
  onSave,
  onDelete,
  muted,
}: {
  exception: Exception;
  onSave: (e: Exception) => void;
  onDelete: () => void;
  muted?: boolean;
}) {
  const [e, setE] = useState(exception);
  useEffect(() => setE(exception), [exception]);
  const dirty =
    e.exception_date !== exception.exception_date ||
    e.label !== exception.label ||
    e.hours !== exception.hours ||
    e.is_closed !== exception.is_closed ||
    (e.note ?? "") !== (exception.note ?? "");

  return (
    <li className={`px-5 py-3 ${muted ? "opacity-60" : ""}`}>
      <div className="grid grid-cols-12 items-center gap-3">
        <input
          type="date"
          className="col-span-3 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={e.exception_date}
          onChange={(ev) => setE({ ...e, exception_date: ev.target.value })}
        />
        <input
          className="col-span-3 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          value={e.label}
          onChange={(ev) => setE({ ...e, label: ev.target.value })}
        />
        <input
          className="col-span-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm disabled:bg-muted/50 disabled:text-text-secondary"
          value={e.hours}
          disabled={e.is_closed}
          onChange={(ev) => setE({ ...e, hours: ev.target.value })}
        />
        <label className="col-span-2 flex items-center gap-1.5 text-xs text-text-secondary whitespace-nowrap">
          <input
            type="checkbox"
            checked={e.is_closed}
            onChange={(ev) =>
              setE({
                ...e,
                is_closed: ev.target.checked,
                hours: ev.target.checked ? "Gesloten" : e.hours === "Gesloten" ? "" : e.hours,
              })
            }
          />
          gesloten
        </label>
        <div className="col-span-2 flex items-center justify-end gap-2">
          <button
            onClick={() => onSave(e)}
            disabled={!dirty}
            className="inline-flex items-center gap-1 rounded-md bg-orange px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="size-3.5" />
            Opslaan
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center justify-center rounded-md border border-border p-1.5 text-text-secondary transition hover:border-red-300 hover:text-red-600"
            aria-label="Verwijderen"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
        <input
          className="col-span-12 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-text-secondary"
          placeholder="Notitie (optioneel)"
          value={e.note ?? ""}
          onChange={(ev) => setE({ ...e, note: ev.target.value })}
        />
        <p className="col-span-12 text-[11px] text-text-secondary">
          {formatDateNL(e.exception_date)}
        </p>
      </div>
    </li>
  );
}
